<template>
  <div
    id="mainContent"
    class="mainContent"
    :class="{
      'is-chrome-ipad': isMobileOperatingSystem() && isChrome,
      'is-ipad': isMobileOperatingSystem(),
      'is-assessment-accelerator': isAssessmentAcceleratorActivity
    }"
  >
    <input
      type="text"
      v-if="isTablet && usingExternalKeyboard && topLevelInput"
      id="external-key-input"
      class="hidden-input"
      @blur="handleBlur"
      autocomplete="off"
      autocorrect="off"
      autocapitalize="none"
      spellcheck="false"
    />
    <div
      v-on:resize="onOrientationChange"
      class="no-portrait"
      :class="{ 'portrait-detected': isPortrait }"
      v-if="isPortrait"
    >
      <p class="portrait-notice">
        Please rotate your device to landscape orientation
      </p>
    </div>
    <div id="checkBrowser" class="check-browser-modal">
      <div class="modal-container">
        <div class="modal-header">
          <h3>Unsupported browser!</h3>
        </div>
      </div>
    </div>

    <div aria-atomic="true" aria-live="polite" id="keyboardingSegmentAlert" role="alert"></div>
    <div aria-atomic="true" aria-live="polite" id="screenReaderAlert" role="alert"></div>

    <div v-if="isInActivity">
      <portal-target name="mobile-activity-header"></portal-target>
    </div>

    <div
      v-if="isInActivity && isAssessmentAcceleratorActivity"
      class="save-activity-button-container"
    >
      <portal-target name="save-activity-button"></portal-target>
    </div>

    <!-- Only show this on QA -->
    <debug-panel
      v-if="(kwtMode === 'curriculum' || kwtMode === 'tester') && allowDebug"
    ></debug-panel>

    <div
      v-if="isInActivity && isAssessmentAcceleratorActivity"
      class="activity-progress-bar-container"
    >
      <portal-target name="activity-progress-bar"></portal-target>
    </div>

    <div v-if="isInActivity" :class="{ 'multiPart-tabs-container': true, tablet: isTablet }">
      <portal-target name="multiPart-tabs"></portal-target>
    </div>

    <tbar></tbar>

    <div
      class="contentPanel"
      v-bind:class="{
        assessmentAccelerator: viewedIntro && isAssessmentAcceleratorActivity,
        'chrome-os': isChromeOS,
        tablet: isTablet
      }"
    >
      <div
        aria-live="assertive"
        class="capslock"
        ref="capslock"
        role="alert"
        :aria-hidden="!isCapsLock"
      >
        <p class="error" v-if="capsLockSeverity === 'error'">
          {{ 'isCapsLock' ? 'Turn caps off' : '' }}
        </p>
        <p class="alert" v-if="capsLockSeverity === 'alert'">
          {{ 'isCapsLock' ? 'Caps lock on' : '' }}
        </p>
      </div>
      <router-view transition-mode="out-in" height="calc(var(--vh, 1vh) * 100)"></router-view>
    </div>

    <div
      v-if="isInActivity || isAssessmentAcceleratorActivity"
      :class="{
        'next-activity-button-container': true,
        tablet: isTablet,
        assessmentAcceleratorTesterActivity: isAssessmentAcceleratorTesterActivity
      }"
    >
      <portal-target name="next-activity-button"></portal-target>
    </div>

    <modal></modal>
  </div>
</template>

<script>
import TweenMax from 'gsap';
import { createNamespacedHelpers, mapGetters } from 'vuex';
import queryString from 'query-string';
import { isChrome, isMobile } from 'react-device-detect';

import Tbar from 'components/utils/Toolbar.vue';
import Modal from 'components/utils/modal.vue';
import DebugPanel from 'components/utils/DebugPanel.vue';
import buildinfo from './buildnum.json';
import googleAnalytics from './modules/google_analytics';

const { VUE_APP_DEBUG } = process.env;

const application = createNamespacedHelpers('application');
const login = createNamespacedHelpers('application/login');
const student = createNamespacedHelpers('student');
const activity = createNamespacedHelpers('activity');
const keyboard = createNamespacedHelpers('keyboard');

export default {
  components: {
    Tbar,
    Modal,
    DebugPanel
  },
  data() {
    return {
      routingModalVisible: false,
      isChrome: isChrome
    };
  },

  computed: {
    ...keyboard.mapState({
      isCapsLock: state => state.capsLock,
      capsLockSeverity: state => state.capsLockSeverity,
      topLevelInput: state => state.topLevelInput
    }),

    ...application.mapState([
      'allowDebug',
      'device',
      'isPortrait',
      'kwtMode',
      'showDebug',
      'toolbarMode'
    ]),

    ...activity.mapState(['viewedIntro', 'readyToStart']),

    ...mapGetters('activity', ['isAssessmentAcceleratorActivity']),

    ...login.mapState(['classroomIds', 'classrooms']),

    isChromeOS() {
      return this.device.isChromeOS;
    },

    isTablet() {
      return this.device.isMobile;
    },

    usingExternalKeyboard() {
      return this.device.usingExternalKeyboard;
    },

    isLiteVersion() {
      const { lite } = queryString.parse(window.location.search) || {};
      return lite === 'true';
    },

    accessibilityMode() {
      const { accessibilityMode } = queryString.parse(window.location.search) || {};
      return accessibilityMode === 'true';
    },

    externalKeyboardEnabled() {
      return this.device.usingExternalKeyboard;
    },

    ipadWithExternalKeyboard() {
      return this.device.isMobile && this.device.usingExternalKeyboard;
    },

    ipadWithoutExternalKeyboard() {
      return this.device.isMobile && !this.device.usingExternalKeyboard;
    },

    isInActivity() {
      return this.readyToStart;
    },

    isAssessmentAcceleratorTesterActivity() {
      return !!(
        this.isAssessmentAcceleratorActivity &&
        (this.kwtMode === 'tester' || ['preview', 'previewLite'].includes(this.kwtMode))
      );
    }
  },

  created() {
    window.addEventListener('resize', this.onOrientationChange, false);
    document.addEventListener('resize', this.onOrientationChange, false);
    document.addEventListener('mousedown', () => {
      document.body.classList.remove('using-keyboard');
    });
    document.addEventListener('keydown', event => {
      if (event.keyCode === 9) {
        document.body.classList.add('using-keyboard');
      }
    });
  },

  mounted() {
    this.init();
  },

  watch: {
    isCapsLock(newValue, oldValue) {
      if (newValue === oldValue) return;

      const self = this;
      if (this.isTablet && this.usingExternalKeyboard && newValue) {
        const { capslock } = this.$refs;
        TweenMax.to(capslock, 0.8, {
          opacity: 1,
          onComplete() {
            TweenMax.to(capslock, 0.6, {
              opacity: 0,
              onComplete() {
                self.setCapsLock(false);
              }
            });
          }
        });

        return;
      }

      const { capslock } = this.$refs;
      if (newValue) {
        TweenMax.to(capslock, 0.5, { opacity: 1 });
      } else {
        TweenMax.to(capslock, 0.5, { opacity: 0 });
      }
    }
  },

  methods: {
    ...keyboard.mapMutations(['setCapsLock']),
    ...activity.mapMutations(['resetActivityStore', 'resetProgress', 'resetViewedIntro']),
    ...student.mapMutations(['setStudent']),
    ...application.mapMutations([
      'chooseMobileKeyboard',
      'determineDeviceCapabilities',
      'setApplication',
      'setKwtMode'
    ]),
    ...login.mapActions(['initSavedData']),
    ...application.mapMutations(['setLiteVersion', 'setAccessibilityMode']),

    async init() {
      googleAnalytics.init();

      const isModernBrowser =
        'querySelector' in document && 'localStorage' in window && 'addEventListener' in window;

      this.setApplication({
        // allowDebug: false,
        allowDebug: VUE_APP_DEBUG === 'true',
        kwtMode: this.detectKwtMode(),
        isModernBrowser,
        buildNum: buildinfo.buildnum,
        appVersion: process.env.VUE_APP_APP_VERSION || '0.0.0'
      });

      this.determineDeviceCapabilities();
      this.onOrientationChange();
      this.setLiteVersion(this.isLiteVersion);
      this.setAccessibilityMode(this.accessibilityMode);

      const self = this;
      // disable backspace from redirecting to browser history
      document.onkeydown = e => {
        const event = e || window.event;
        if (event.keyCode === 8 || event.key === 'Backspace') {
          const elements = 'HTML, BODY, TABLE, TBODY, TR, TD, DIV';
          const d = event.srcElement || event.target;
          const regex = new RegExp(d.tagName.toUpperCase());
          if (regex.test(elements)) {
            event.preventDefault();
          }
        }
        // prevent default behavior for TAB key, when necessary
        if (event.keyCode === 9 && self.tabKeyDefaultBehaviorPrevented) {
          event.preventDefault();
        }

        const { keyCode } = event;
        if (self.firefoxQuickSearchKeysPrevented && (keyCode === 191 || keyCode === 222)) {
          event.preventDefault();
        }
      };

      document.onkeypress = e => {
        const event = e || window.event;
        if (event.keyCode === 8 || event.key === 'Backspace') {
          const elements = 'HTML, BODY, TABLE, TBODY, TR, TD, DIV';
          const d = event.srcElement || event.target;
          const regex = new RegExp(d.tagName.toUpperCase());
          if (regex.test(elements)) {
            event.preventDefault();
          }
        }
      };

      if (this.oldBrowser()) {
        this.$router.replace({ path: '/unsupported', params: {} });
        return;
      }
    },

    onOrientationChange() {
      this.setApplication({
        isPortrait: window.innerHeight > window.innerWidth
      });
    },

    detectKwtMode() {
      let mode = 'curriculum';
      if (this.$route.path.startsWith('/preview?')) {
        mode = 'preview';
      }
      return mode;
    },

    isMobileOperatingSystem() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      // Windows Phone must come first because its UA also contains "Android"
      // can later be reworked to detect which mobile OS
      if (/windows phone/i.test(userAgent)) {
        return true;
      }
      if (/android/i.test(userAgent)) {
        return true;
      }
      // iOS detection from: http://stackoverflow.com/a/9039885/177710
      if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return true;
      }

      if (isMobile) return true;

      return false;
    },

    handleBlur() {
      document.getElementById('external-key-input').focus();
    },

    oldBrowser() {
      // Check and see if the browser is unsupported
      /*eslint-disable */
      var ie = (function() {
        var v = 5;
        var div = document.createElement('div');
        var a = div.all || [];

        while (((div.innerHTML = '<!--[if gt IE ' + ++v + ']><i></i><![endif]-->'), a[0])) {
          return v > 6 ? v : !v;
        }
      })();

      // Check to see if browser is IE10
      var isIE10 = false;
      // This needs to have no-spaces after opening and before closing the comment in order to work
      /*@cc_on
          if (/^10/.test(@_jscript_version)) {
            isIE10 = true
          }
        @*/
      /* eslint-enable */

      // Alert the users if their browser is less then IE10
      if (ie < 10 || isIE10) return true;
      return false;
    }
  }
};
</script>

<style lang="scss">
@import './assets/sass/kwt-global.scss';

.activity-progress-bar-container {
  height: 25px;
  left: 50%;
  max-width: 40%;
  position: fixed;
  top: 35px;
  transform: translateX(-50%);
  width: 100%;
  z-index: 1000;

  .activity-progress-bar {
    height: 25px;
  }

  @include activityProgressBarReflow(986px, 0.8);
  @include activityProgressBarReflow(863px, 0.6);
  @include activityProgressBarReflow(690px, 0.5);
  @include activityProgressBarReflow(432px, 0.3);
}

.next-activity-button-container {
  position: fixed;
  top: 5px;
  margin: 10px 8px;
  right: 80px;
  z-index: 1000;

  button {
    padding: 0px;
  }

  &:not(.tablet) {
    top: 50%;
    right: 55px;
  }

  &:not(.assessmentAcceleratorTesterActivity) {
    right: 5px;
  }

  @include nextActivityButtonReflow(1152px, 1);
  @include nextActivityButtonReflow(863px, 0.74);
  @include nextActivityButtonReflow(690px, 0.5);
  @include nextActivityButtonReflow(432px, 0.25);
}

.multiPart-tabs-container {
  position: fixed;
  right: 13px;
  top: 132px;
  -webkit-transform: none;
  -moz-transform: none;
  -ms-transform: none;
  -o-transform: none;
  transform: none;
  z-index: 1000;

  &:not(.tablet) {
    right: calc(((100vw - 1028px) / 2));
    top: calc(10vh - 5px);
    -webkit-transform: translateX(100%);
    -moz-transform: translateX(100%);
    -ms-transform: translateX(100%);
    -o-transform: translateX(100%);
    transform: translateX(100%);
  }
}

.save-activity-button-container {
  position: fixed;
  top: 5px;
  left: 5px;
  margin: 10px 8px;
  z-index: 1000;

  button {
    padding: 0px;
  }

  @include saveActivityButtonReflow(986px, 1);
  @include saveActivityButtonReflow(863px, 0.74);
  @include saveActivityButtonReflow(690px, 0.5);
  @include saveActivityButtonReflow(432px, 0.25);
}
</style>
